import {
  registerDecorator,
  type ValidationArguments,
  type ValidationOptions,
} from 'class-validator';

const isComparable = (value: unknown): value is number | Date =>
  typeof value === 'number' || value instanceof Date;

/**
 * Class validator that checks if a number or date is greater than the value of another property.
 * @param property          Property name to compare to.
 * @param validationOptions
 * @returns
 */
export function IsGreaterThan<T>(
  property: keyof T,
  validationOptions?: ValidationOptions & { relatedValueNullable?: boolean },
) {
  return function (object: unknown, propertyName: string) {
    registerDecorator({
      name: 'isGreaterThan',
      target: object.constructor,
      propertyName,
      constraints: [property],
      options: validationOptions,
      validator: {
        validate(value: unknown, args: ValidationArguments) {
          const [relatedPropertyName] = args.constraints;
          const relatedValue = (args.object as unknown)[relatedPropertyName];

          if (validationOptions?.relatedValueNullable && relatedValue == null)
            return true;

          return (
            isComparable(value) &&
            isComparable(relatedValue) &&
            typeof value === typeof relatedValue &&
            value.valueOf() > relatedValue.valueOf()
          );
        },

        defaultMessage(args: ValidationArguments) {
          const [relatedPropertyName] = args.constraints;
          return `${args.property} must be greater than ${relatedPropertyName}`;
        },
      },
    });
  };
}
